/*
 * Purpose: updates a Drop countdown from Shopify-rendered server time and refreshes state at the boundary.
 * Dependencies: data attributes emitted by snippets/drop-status.liquid; no cart authority is granted here.
 * Rendered by: sections/drop-experience.liquid.
 * Related data: countdown_enabled, launch_at, end_at, and the normalized Drop state.
 */

const dropStatusComponentName = 'drop-status';
const dropStatusSecondMs = 1000;
const dropStatusMinuteSeconds = 60;
const dropStatusHourSeconds = 3600;
const dropStatusDaySeconds = 86400;

if (window.customElements && !customElements.get(dropStatusComponentName)) {
  customElements.define(dropStatusComponentName, class extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-countdown') || this.initialized) return;

      const targetEpoch = Number(this.dataset.targetEpoch) * dropStatusSecondMs;
      const serverEpoch = Number(this.dataset.serverEpoch) * dropStatusSecondMs;
      if (!Number.isFinite(targetEpoch) || !Number.isFinite(serverEpoch)) return;

      if (this.serverEpoch !== serverEpoch || !Number.isFinite(this.startedAt)) {
        this.startedAt = performance.now();
      }
      this.targetEpoch = targetEpoch;
      this.serverEpoch = serverEpoch;

      this.days = this.querySelector('[data-countdown-days]');
      this.hours = this.querySelector('[data-countdown-hours]');
      this.minutes = this.querySelector('[data-countdown-minutes]');
      this.seconds = this.querySelector('[data-countdown-seconds]');
      this.initialized = true;

      this.handleVisibilityChange ||= this._handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this._tick();
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.clearTimeout(this.timer);
      this.initialized = false;
    }

    _handleVisibilityChange() {
      if (document.hidden) {
        window.clearTimeout(this.timer);
        return;
      }

      this._tick();
    }

    _tick() {
      window.clearTimeout(this.timer);

      const estimatedServerNow = this.serverEpoch + (performance.now() - this.startedAt);
      const remaining = Math.max(0, this.targetEpoch - estimatedServerNow);
      const totalSeconds = Math.ceil(remaining / dropStatusSecondMs);

      this._render(totalSeconds);

      if (remaining <= 0) {
        this._refreshStateOnce();
        return;
      }

      if (!document.hidden) this.timer = window.setTimeout(() => this._tick(), dropStatusSecondMs);
    }

    _render(totalSeconds) {
      const days = Math.floor(totalSeconds / dropStatusDaySeconds);
      const hours = Math.floor((totalSeconds % dropStatusDaySeconds) / dropStatusHourSeconds);
      const minutes = Math.floor((totalSeconds % dropStatusHourSeconds) / dropStatusMinuteSeconds);
      const seconds = totalSeconds % dropStatusMinuteSeconds;

      if (this.days) this.days.textContent = String(days).padStart(2, '0');
      if (this.hours) this.hours.textContent = String(hours).padStart(2, '0');
      if (this.minutes) this.minutes.textContent = String(minutes).padStart(2, '0');
      if (this.seconds) this.seconds.textContent = String(seconds).padStart(2, '0');
    }

    _refreshStateOnce() {
      const refreshKey = `drop-status-refreshed:${this.targetEpoch}`;
      try {
        if (sessionStorage.getItem(refreshKey)) return;
        sessionStorage.setItem(refreshKey, 'true');
      } catch (error) {
        console.warn('Drop status refresh could not be recorded.', error);
        return;
      }
      window.location.reload();
    }
  });
}
